import React from 'react'
import { observer } from 'mobx-react'
import {
  Card,
  CardContent,
  CardHeader,
  CardHeaderActions,
  CardTitle,
} from 'components/Card'
import Button from 'components/Button'
import { JobReturnAmountsYearDTO } from 'jobs/dtos/job'
import { FinalReturnAmountsCardViewModel } from './FinalReturnAmountsCardViewModel'
import FinalReturnAmountsDialog from '../FinalReturnAmountsDialog'
import { formatAmount } from './formatAmount'
import styles from './FinalReturnAmountsCard.sass'

interface Props {
  vm: FinalReturnAmountsCardViewModel
}

const SOURCE_LABELS: Record<string, string> = {
  EXTRACTED: 'Auto-extracted',
  MANUAL: 'Manually entered',
}

export default observer(function FinalReturnAmountsCard({ vm }: Props) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Final return amounts</CardTitle>
        {vm.canEdit && (
          <CardHeaderActions>
            <Button
              type="secondary"
              size="small"
              onClick={vm.edit}
              data-test="final-return-amounts-card-edit"
            >
              Edit
            </Button>
          </CardHeaderActions>
        )}
      </CardHeader>

      <CardContent scroll>
        {vm.years.map((year) => (
          <YearBlock key={year.tax_year} year={year} />
        ))}
      </CardContent>

      <FinalReturnAmountsDialog vm={vm.dialogVM} submitLabel="Save" />
    </Card>
  )
})

const YearBlock = observer(function YearBlock({
  year,
}: {
  year: JobReturnAmountsYearDTO
}) {
  const sourceLabel = SOURCE_LABELS[year.source]

  return (
    <div
      className={styles.yearBlock}
      data-test={`final-return-amounts-card-year-${year.tax_year}`}
    >
      <div className={styles.yearHeader}>
        <span className={styles.yearTitle}>Tax year {year.tax_year}</span>
        {sourceLabel && (
          <span className={styles.sourceBadge}>{sourceLabel}</span>
        )}
      </div>

      {year.federal_amount !== undefined && (
        <div className={styles.amountRow}>
          <span className={styles.amountLabel}>Federal</span>
          <span className={styles.amountValue}>
            {formatAmount(year.federal_amount)}
          </span>
        </div>
      )}

      {year.state_amounts.map((state) => (
        <div key={state.state} className={styles.amountRow}>
          <span className={styles.amountLabel}>{state.state}</span>
          <span className={styles.amountValue}>
            {formatAmount(state.amount)}
          </span>
        </div>
      ))}
    </div>
  )
})
